import type { Asset, Scene } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { checkpointVideo } from 'js/src/data/video-data';
import Flags from 'js/src/libs/flags';
import uuid from 'js/src/libs/uuid';
import { copyAssetToContainerAsSticker } from 'js/src/luminary/copy-asset-utils';
import { saveRawLuminary } from 'js/src/luminary/index';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    canFreezeNonTextForMatchCut,
    hasAdjacentMorphEnabledScene,
    resolveMatchCutPairs,
} from 'js/src/luminary/luminary-selectors';
import type { MorphPairing, MorphRecommendation } from 'js/src/luminary/morph-recommendation';
import { getTextMorphUnits, scoreScenePairForMorph } from 'js/src/luminary/morph-recommendation';

export const toggleSceneMorphEnabledOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);

        if (!scene) {
            return;
        }
        luminary.batchRun(() => {
            scene.morphEnabled = !scene.morphEnabled;
            if (!scene.morphEnabled) {
                // Leave no trace of a match-cut, so a reload or re-enable can't replay a stale one.
                scene.matchCutTechnique = VideoData.MATCH_CUT_NONE;
                clearMatchCutArtifacts(scene, scene.nextScene);
            }
        });
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

const MATCH_CUT_MORPH_INTERPOLATION = VideoData.EASE_IN_OUT_INTERPOLATION_PRESET;

function initMatchCutDuplicate(duplicate: Asset): void {
    duplicate.alpha = 0;
    duplicate.isMatchCutDuplicate = true;
}

// `loadAsSticker` forces `createdFrom = STICKER`, which suppresses MediaAsset's full-bleed
// cover-centering. A duplicated full-bleed media would then render its (oversized) content at a
// different offset under the mask than the real media, so the image jumps at the scene boundary.
// Restoring the source's `createdFrom` makes the duplicate center its content identically.
function restoreMediaDuplicateCreatedFrom(duplicate: Asset, source: Asset): void {
    if (VideoData.isMediaAsset(duplicate) && VideoData.isMediaAsset(source)) {
        duplicate._createdFromCustomLoad(source.createdFrom);
    }
}

// Place `duplicate` (a copy of `original`) so its visual center lands on `positionSource`'s visual
// center, using the already-laid-out `original` and `positionSource` bounds (NOT the freshly-created
// duplicate's, which aren't built yet inside batchRun — reading them would require a force-build that
// breaks the duplicate's morph).
function placeMorphDuplicate(duplicate: Asset, original: Asset, positionSource: Asset): void {
    const targetBounds = positionSource.getBounds();
    const originalBounds = original.getBounds();
    const { anchor } = original.transformation;

    duplicate.transformation.position.x =
        targetBounds.x +
        targetBounds.width / 2 -
        originalBounds.width / 2 +
        anchor.x * originalBounds.width;
    duplicate.transformation.position.y =
        targetBounds.y +
        targetBounds.height / 2 -
        originalBounds.height / 2 +
        anchor.y * originalBounds.height;
    initMatchCutDuplicate(duplicate);
}

type MatchCutDuplicatePlacement = { original: Asset; positionSource: Asset };
const matchCutDuplicatePlacements = new WeakMap<Asset, MatchCutDuplicatePlacement>();

const realignArmedLuminaries = new WeakSet<Luminary>();

function realignMatchCutDuplicates(luminary: Luminary): void {
    const placements = luminary.scenes.flatMap((scene) =>
        scene.stickers.getAssets().flatMap((sticker) => {
            const placement = matchCutDuplicatePlacements.get(sticker);
            if (!placement || placement.original.destroyed || placement.positionSource.destroyed) {
                return [];
            }
            return [{ duplicate: sticker, placement }];
        }),
    );
    if (placements.length === 0) {
        return;
    }
    luminary.batchRun(() => {
        placements.forEach(({ duplicate, placement }) => {
            placeMorphDuplicate(duplicate, placement.original, placement.positionSource);
        });
    });
}

function armMatchCutDuplicateRealignment(luminary: Luminary): void {
    if (realignArmedLuminaries.has(luminary)) {
        return;
    }
    realignArmedLuminaries.add(luminary);
    luminary.on('stateChanged', () => realignMatchCutDuplicates(luminary));
}

// Clear inherited entry/exit animations so the alpha fade can't override the duplicate's frozen alpha,
// but keep the mask reveal: mask.transition isn't serialized, so without it the frozen mask stays clipped.
function freezeDuplicateAnimations(duplicate: Asset): void {
    duplicate.animationConfig = duplicate.animationConfig.filter((config) =>
        config.drivenProperty.includes('mask.transition'),
    );
    if ('objectAnimation' in duplicate) {
        duplicate.objectAnimation = [];
    }
}

function createFrozenDuplicate(assetToFreeze: Asset, targetContainer: Scene): Asset {
    const duplicate = copyAssetToContainerAsSticker(assetToFreeze, targetContainer);
    initMatchCutDuplicate(duplicate);
    freezeDuplicateAnimations(duplicate);
    restoreMediaDuplicateCreatedFrom(duplicate, assetToFreeze);
    return duplicate;
}

// Duplicates are excluded from save but live in-session. Only the scene's own originals have their
// morph props reset — nextScene only ever receives duplicates, never modified originals.
function clearMatchCutArtifacts(scene: Scene, nextScene: Scene | undefined): void {
    [scene, nextScene].forEach((target) => {
        if (!target) return;
        target.stickers
            .getAssets({ deep: false })
            .filter((sticker) => sticker.isMatchCutDuplicate)
            .forEach((duplicate) => {
                target.stickers.removeChild(duplicate);
                duplicate.destroy();
            });
    });

    scene.getAssets({ deep: true }).forEach((asset) => {
        asset.morphAlphaOnly = false;
        asset.morphAlphaStep = false;
        asset.morphInterpolation = VideoData.LINEAR_INTERPOLATION_PRESET;
    });

    // The morph-prop reset above only touches `scene`, but the freeze sets `isMatchCutFrozen` on assets
    // across BOTH scenes — so clear it on both here.
    [scene, nextScene].forEach((target) => {
        if (!target) return;
        target.getAssets({ deep: true }).forEach((asset) => {
            asset.isMatchCutFrozen = false;
        });
    });
}

// Add nextScene's candidates first so the duplicates stack in the same order they do in nextScene.
function freezeAssetsAcrossCut(
    scene: Scene,
    nextScene: Scene,
    candidates: (container: Scene) => Asset[],
): void {
    candidates(nextScene).forEach((targetAsset) => {
        const duplicate = createFrozenDuplicate(targetAsset, scene);
        duplicate.morphAlphaOnly = true;
        duplicate.morphAlphaStep = true;
    });

    candidates(scene).forEach((sourceAsset) => {
        createFrozenDuplicate(sourceAsset, nextScene);
        sourceAsset.morphAlphaOnly = true;
        sourceAsset.morphAlphaStep = true;
    });
}

function freezeNonTextAcrossCut(scene: Scene, nextScene: Scene): void {
    // !isMatchCutDuplicate is needed alongside !isStickerAsset: a media duplicate restores its source's
    // createdFrom, so it doesn't read as a sticker and would otherwise be re-frozen on the second pass.
    const freezable = (container: Scene) =>
        container
            .getAssets()
            .filter(
                (asset) =>
                    !asset.isMatchCutDuplicate &&
                    !VideoData.isStickerAsset(asset) &&
                    canFreezeNonTextForMatchCut(asset),
            );
    freezeAssetsAcrossCut(scene, nextScene, freezable);
}

// A text morph unit is a whole group (or a bare text) — see getTextMorphUnits.
function freezeUnmatchedTextAcrossCut(
    scene: Scene,
    nextScene: Scene,
    pairings: MorphPairing[],
): void {
    const pairedUnitIds = new Set(
        pairings.flatMap((pairing) => [pairing.sourceId, pairing.targetId]),
    );
    const unmatchedUnits = (container: Scene) =>
        getTextMorphUnits(container).filter((unit) => !pairedUnitIds.has(unit.id));
    freezeAssetsAcrossCut(scene, nextScene, unmatchedUnits);
}

function duplicateMatchCutAssets(scene: Scene, nextScene: Scene, pairings: MorphPairing[]): void {
    const textPairs = resolveMatchCutPairs(scene, nextScene, pairings);
    if (textPairs.length === 0) {
        return;
    }

    const stickerIdsBeforeDuplication = new Set(
        [scene, nextScene].flatMap((target) =>
            target.stickers.getAssets().map((asset) => asset.id),
        ),
    );

    // Freeze the non-text frame FIRST so the text duplicates, created after, render on top of it
    // (stickers stack in creation order).
    freezeNonTextAcrossCut(scene, nextScene);

    textPairs.forEach(({ sourceAsset, targetAsset }) => {
        const targetSideDuplicate = copyAssetToContainerAsSticker(sourceAsset, nextScene);
        const sceneSideDuplicate = copyAssetToContainerAsSticker(targetAsset, scene);
        placeMorphDuplicate(targetSideDuplicate, sourceAsset, targetAsset);
        placeMorphDuplicate(sceneSideDuplicate, targetAsset, sourceAsset);
        matchCutDuplicatePlacements.set(targetSideDuplicate, {
            original: sourceAsset,
            positionSource: targetAsset,
        });
        matchCutDuplicatePlacements.set(sceneSideDuplicate, {
            original: targetAsset,
            positionSource: sourceAsset,
        });
        sourceAsset.morphInterpolation = MATCH_CUT_MORPH_INTERPOLATION;
        sceneSideDuplicate.morphInterpolation = MATCH_CUT_MORPH_INTERPOLATION;
        sourceAsset.morphAlphaStep = true;
        sceneSideDuplicate.morphAlphaStep = true;
    });

    freezeUnmatchedTextAcrossCut(scene, nextScene, pairings);

    // Mark every sticker the duplication added (deeply — a duplicated group's children are stickers
    // too) so none serialize, and make each non-editable so the editor overlay neither selects a
    // ghost nor lets it cover the real asset underneath.
    [scene, nextScene].forEach((target) => {
        target.stickers.getAssets().forEach((sticker) => {
            if (!stickerIdsBeforeDuplication.has(sticker.id)) {
                sticker.isMatchCutDuplicate = true;
                sticker.isEditable = false;
            }
        });
    });

    // Restore the scene's own content that a reload left stranded fully invisible (a save can bake a
    // mid-fade alpha 0 onto match-cut text/shapes). alpha 0 is never an intentional resting value, so this
    // is safe and leaves intentional partial alphas untouched; the duplicates keep their own alpha 0.
    scene
        .getAssets({ deep: true })
        .filter((asset) => !asset.isMatchCutDuplicate && asset.alpha === 0)
        .forEach((asset) => {
            asset.alpha = 1;
        });

    // Hold each asset's own entry/exit/background animation at rest so the scene reads as a settled
    // snapshot (isMatchCutFrozen); a morph-paired asset still slides via its separate morph animation.
    [scene, nextScene].forEach((target) => {
        target.getAssets({ deep: true }).forEach((asset) => {
            asset.isMatchCutFrozen = true;
        });
    });
}

// Match-cut duplicates are transient stickers excluded from save, but `morphEnabled` persists — so a
// reloaded match-cut scene has morph on with nothing to morph into. Recreate the duplicates for every
// morph-enabled scene the recommendation still resolves to a match-cut. Re-scoring is safe: a plain
// true-morph scene yields only true-morph pairs, which produce no duplicates (resolveMatchCutPairs
// keeps match-cut pairs only).
export function rehydrateMatchCutDuplicates(luminary: Luminary): void {
    // Scene sequences and swap-scene set (and persist) scene.morphEnabled WITHOUT the morph_transitions
    // flag, so without this gate, loading any video containing one would re-score those morphs and inject
    // match-cut duplicates for users who never opted into the feature.
    if (!Flags.canUseMorphTransitions()) return;

    if (!luminary.template.transitionsEnabled) return;

    // A saved video never carries duplicates, so any present means rehydration already ran (or the
    // match-cut was just applied in-session) — skip the whole pass. A per-scene guard would be wrong
    // for a morph chain: each scene receives its predecessor's partner duplicates before its own turn.
    const duplicatesAlreadyExist = luminary.scenes.some((scene) =>
        scene.stickers.getAssets().some((sticker) => sticker.isMatchCutDuplicate),
    );
    if (duplicatesAlreadyExist) return;

    const rehydratedScenes = luminary.scenes.flatMap((scene) => {
        const { nextScene } = scene;
        if (!scene.morphEnabled || !nextScene) return [];
        if (scene.matchCutTechnique === VideoData.MATCH_CUT_NONE) return [];

        // The persisted matchCutTechnique — not the score — is what decides we replay this scene. We still
        // call scoreScenePairForMorph to re-derive the pairings from the current layout (geometry-stable),
        // but ignore its recommend/threshold verdict, so scoring-algorithm changes can't restyle a saved video.
        const { pairings } = scoreScenePairForMorph(scene, nextScene);

        luminary.batchRun(() => {
            duplicateMatchCutAssets(scene, nextScene, pairings);
        });
        return [scene];
    });
    if (rehydratedScenes.length > 0) {
        armMatchCutDuplicateRealignment(luminary);
    }
}

export const applyRecommendedMorphOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): MorphRecommendation | null => {
        const scene = luminary.getSceneById(sceneId);
        const nextScene = scene?.nextScene;
        if (!scene || !nextScene) {
            return null;
        }

        const recommendation = scoreScenePairForMorph(scene, nextScene);
        if (!recommendation.recommended || scene.morphEnabled) {
            return recommendation;
        }

        // Refuse if a neighbor scene already morphs: a 3+-scene morph chain is out of scope, and the
        // toggle-off cleanup (clearMatchCutArtifacts) only untangles a single pair.
        if (hasAdjacentMorphEnabledScene(scene)) {
            return {
                recommended: false,
                score: 0,
                techniqueId: null,
                disqualifiedReason: 'consecutive-morph-limit',
                pairings: [],
            };
        }

        luminary.batchRun(() => {
            duplicateMatchCutAssets(scene, nextScene, recommendation.pairings);
            scene.morphEnabled = true;
            scene.matchCutTechnique = recommendation.techniqueId ?? VideoData.MATCH_CUT_NONE;
        });
        armMatchCutDuplicateRealignment(luminary);
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
        return recommendation;
    },
);

export const syncMatchCutsWithTransitionsEnabledOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        transitionsEnabled: boolean,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        if (!Flags.canUseMorphTransitions()) {
            return;
        }
        const matchCutScenes = luminary.scenes.filter(
            (scene) => scene.matchCutTechnique !== VideoData.MATCH_CUT_NONE,
        );
        if (matchCutScenes.length === 0) {
            return;
        }

        luminary.batchRun(() => {
            matchCutScenes.forEach((scene) => {
                if (transitionsEnabled) {
                    scene.morphEnabled = true;
                } else if (scene.morphEnabled) {
                    scene.morphEnabled = false;
                    clearMatchCutArtifacts(scene, scene.nextScene);
                }
            });
            if (transitionsEnabled) {
                rehydrateMatchCutDuplicates(luminary);
            }
        });

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);
